import { Suspense, lazy, useEffect, useState, useCallback, useRef } from "react"
import { Routes, Route, useNavigate } from "react-router-dom"

import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react"
import { EventType, type IPublicClientApplication } from "@azure/msal-browser"
import { CustomNavigationClient } from "./utils/navigationClient.util"
import { createSignalRContext } from "react-signalr/signalr"

import { DatabaseProvider } from "@nozbe/watermelondb/DatabaseProvider"
import { database } from "./model/database"

import DashboardLayout from "./layouts/Dashboard.layout"
import { setupInterceptors, getAuthToken } from "./services/api.service"
import { BASE_API_WS_URL, WORKSPACE } from "./constants"
import { userService } from "./services"
import { useLocalStorage } from "usehooks-ts"
import ScrollToTop from "./components/Atoms/ScrollToTop/ScrollToTop.component"
import { msalInstance } from "."
import type { ISettings } from "./contexts/settings.context"
import { LicenseInfo } from "@mui/x-license"
import { SyncProvider } from "./providers/SyncProvider"
import { securityService } from "./services/security.service"
import { PermissionBasedRoutes } from "@utilisourcepackagelibdev/utilisourcepackagelib"
import { transitions, positions, Provider as AlertProvider } from "react-alert"
import {
  Alert,
  Snackbar,
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import VideoFeed from "./pages/Video/VideoFeed.page"
import VideoPlayer from "./pages/Video/VideoPlayer.page"
import { datadogRum } from "@datadog/browser-rum"

// Import the local IUser interface that has the disabled property
import type { IUser } from "./interfaces"

type AppProps = {
  pca: IPublicClientApplication
}

const SignalRContext = createSignalRContext()

//? Lazy Loaded Routes
const Home = lazy(async () => import("./pages/Home/Home.page"))
const FormsPage = lazy(async () => import("./pages/Forms/Forms.page"))
const FormBuilderPage = lazy(async () => import("./pages/FormBuilder/FormBuilder.page"))
const FormRendererPage = lazy(async () => import("./pages/FormRenderer/FormRenderer.page"))
const SurveysPage = lazy(async () => import("./pages/Surveys/Surveys.page"))
const FeaturesPage = lazy(async () => import("./pages/Features/Features.page"))
const ReportsPage = lazy(async () => import("./pages/Reports/Reports.page"))
const SurveyDetailPage = lazy(async () => import("./pages/Surveys/SurveyDetail2.page"))
const PointsPage = lazy(async () => import("./pages/Points/Points.page"))
const WorldPage = lazy(async () => import("./pages/World/World.page"))
const WorldsPage = lazy(async () => import("./pages/Worlds/Worlds.page"))
const ProjectsPage = lazy(async () => import("./pages/Projects/Projects.page"))
const UsersPage = lazy(async () => import("./pages/Users/Users.page"))
const LogInPage = lazy(async () => import("./pages/LogIn/LogIn.page"))
const PermissionsPage = lazy(async () => import("./pages/Permissions/Permissions.page"))
const ProjectMapPage = lazy(async () => import("./pages/Projects/ProjectMap.page"))
const DatabaseMetricsPage = lazy(async () => import("./pages/Settings/DatabaseMetrics.page"))

function App({ pca }: AppProps) {
  const navigate = useNavigate()
  const navigationClient = new CustomNavigationClient(navigate)
  const [_user, setUser] = useLocalStorage<IUser | null>("user", null)
  const [activeAccount, setActiveAccount] = useState(pca.getActiveAccount())
  const [token, setToken] = useState<string>("")
  const [_settings, setSettings] = useLocalStorage<ISettings | null>("settings", null)

  // User disabled notification state
  const [showDisabledAlert, setShowDisabledAlert] = useState(false)
  const [logoutCountdown, setLogoutCountdown] = useState(15)
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null)

  pca.setNavigationClient(navigationClient)

  // Function to start the logout countdown
  const startLogoutCountdown = useCallback(() => {
    setShowDisabledAlert(true)
    setLogoutCountdown(15)

    // Clear any existing timer
    if (logoutTimerRef.current) {
      clearInterval(logoutTimerRef.current)
    }

    // Start a new countdown timer
    logoutTimerRef.current = setInterval(() => {
      setLogoutCountdown((prevCount) => {
        if (prevCount <= 1) {
          // Clear the interval when we reach 0
          if (logoutTimerRef.current) {
            clearInterval(logoutTimerRef.current)
          }
          // Perform the actual logout
          handleLogout()
          return 0
        }
        return prevCount - 1
      })
    }, 1000)
  }, [])

  // Cleanup the timer when component unmounts
  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) {
        clearInterval(logoutTimerRef.current)
      }
    }
  }, [])

  // Handle logout when disabled user is detected
  const handleLogout = useCallback(() => {
    // Clear any active timers
    if (logoutTimerRef.current) {
      clearInterval(logoutTimerRef.current)
    }

    // Reset notification state
    setShowDisabledAlert(false)

    // Clear user from local storage
    setUser(null)

    // Log out from MSAL
    pca.logoutRedirect({
      postLogoutRedirectUri: "/",
    })
  }, [pca, setUser])

  // Function to handle immediate logout (user clicked button)
  const handleImmediateLogout = useCallback(() => {
    handleLogout()
  }, [handleLogout])

  // Function to handle user disabled detection
  const handleUserDisabled = useCallback(() => {
    startLogoutCountdown()
  }, [startLogoutCountdown])

  // Setup API interceptors with user disabled handler
  setupInterceptors(handleUserDisabled)

  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY || "")

  useEffect(() => {
    // This will be run on component mount
    const callbackId = msalInstance.addEventCallback((message) => {
      // This will be run every time an event is emitted after registering this callback
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        const result = message.payload
        console.log(result)
        setActiveAccount(pca.getActiveAccount())
      }
    })

    return () => {
      // This will be run on component unmount
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId)
      }
    }
  }, [])

  useEffect(() => {
    async function getToken() {
      const { token, user } = await getAuthToken()
      if (user && user.email) {
        // Checks if we have an exiting user
        const exist = await securityService.getUser(user.email)
        if (exist) {
          // Check if user is disabled
          if ("disabled" in exist && exist.disabled === true) {
            handleUserDisabled()
          }
          setUser(exist)
        } else await userService.addUser(user)

        console.log("DataDog User", user)

        datadogRum.setUser({
          id: user.externalId,
          userId: user.userId,
          name: user.userName,
          email: user.email,
          roleId: user.roleId,
          roleName: user.roleName,
          deviceId: user.deviceId,
          // scopeType: userContext.ContextUser.ScopeType,
        })
      }
      setToken(token)
    }
    getToken()
  }, [activeAccount, handleUserDisabled])

  useEffect(() => {
    // If sidebar value inside localstorage has a cached node value,
    // this will throw an error.
    // We need to check and clear it.
    if (_settings && _settings.sidebar) {
      setSettings({
        ..._settings,
        sidebar: null,
      })
    }
  }, [_settings?.sidebar])

  const options = {
    // you can also just use 'bottom center'
    position: positions.TOP_RIGHT,
    timeout: 5000,
    offset: "30px",
    // you can also just use 'scale'
    transition: transitions.FADE,
  }

  return (
    <MsalProvider instance={pca}>
      <AlertProvider
        {...options}
        template={({ message, style, close }) => (
          <Alert onClose={close} style={{ ...style }}>
            {message}
          </Alert>
        )}>
        <DatabaseProvider database={database}>
          <ScrollToTop />

          <div id="App">
            <AuthenticatedTemplate>
              <SyncProvider>
                <Suspense fallback={<div>Loading...</div>}>
                  <SignalRContext.Provider
                    connectEnabled={!!token && token.length > 0}
                    automaticReconnect={true}
                    accessTokenFactory={() => token}
                    dependencies={[token]} //remove previous connection and create a new connection if changed
                    logMessageContent={false}
                    url={BASE_API_WS_URL + "/survey-hub"}>
                    <Routes>
                      <Route path={"/"} element={<DashboardLayout />}>
                        <Route index element={<Home />} />

                        <Route path="/dashboard" element={<PermissionBasedRoutes user={_user} workspace={WORKSPACE} />}>
                          <Route path={"worlds"} element={<WorldsPage />} />
                          <Route path={"projects"} element={<ProjectsPage />} />
                          <Route path={"users"} element={<UsersPage />} />
                          <Route path="templates">
                            <Route index element={<FormsPage />} />
                            <Route path={`:formId`} element={<FormBuilderPage />} />
                          </Route>
                        </Route>

                        <Route path="/permissions" element={<PermissionsPage />} />

                        <Route path={`/project`}>
                          <Route index element={<Home />} />
                          <Route path={`map`} element={<ProjectMapPage />} />
                          <Route path="jobs">
                            <Route index element={<SurveysPage />} />
                            <Route path={`:id`} element={<SurveyDetailPage />} />
                          </Route>
                          <Route path="points" element={<PointsPage />} />
                          <Route path="features" element={<FeaturesPage />} />
                          <Route path="reports" element={<ReportsPage />} />
                        </Route>

                        <Route path={"/forms"}>
                          <Route index element={<FormsPage />} />
                          <Route path={`builder/:id`} element={<FormBuilderPage />} />
                          <Route path={`renderer/:id`} element={<FormRendererPage />} />
                        </Route>
                        <Route path={"/features"}>
                          <Route index element={<FeaturesPage />} />
                        </Route>
                        <Route path={"/settings"}>
                          <Route index path={`database`} element={<DatabaseMetricsPage />} />
                        </Route>
                        <Route path={"/video"}>
                          <Route index path={`feed`} element={<VideoFeed />} />
                          <Route index path={`player/:videoId`} element={<VideoPlayer />} />
                        </Route>

                        <Route path={"/points"} element={<PointsPage />} />

                        <Route path={`world/:step?`} element={<WorldPage />} />
                        <Route path={"/worlds"} element={<WorldsPage />} />
                      </Route>
                    </Routes>
                  </SignalRContext.Provider>
                </Suspense>
              </SyncProvider>
            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
              <LogInPage />
            </UnauthenticatedTemplate>
          </div>

          {/* User Disabled Alert Dialog */}
          <Dialog
            open={showDisabledAlert}
            aria-labelledby="disabled-user-dialog-title"
            aria-describedby="disabled-user-dialog-description"
            disableEscapeKeyDown
            disablePortal>
            <DialogTitle id="disabled-user-dialog-title" sx={{ bgcolor: "error.main", color: "white" }}>
              Account Disabled
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="disabled-user-dialog-description" sx={{ mt: 2 }}>
                Your account has been disabled by an administrator. You will be automatically logged out in{" "}
                {logoutCountdown} seconds.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleImmediateLogout} variant="contained" color="primary">
                Logout Now
              </Button>
            </DialogActions>
          </Dialog>
        </DatabaseProvider>
      </AlertProvider>
    </MsalProvider>
  )
}

export { SignalRContext }

export default App
